/**
 * IndexedDB-based log storage service.
 * Provides persistent storage for application logs with automatic retention management.
 *
 * Uses lazy initialization with a singleton pattern to avoid blocking app startup.
 */

import { openDB, type IDBPDatabase } from 'idb'
import type {
  LogEntry,
  CreateLogEntryOptions,
  LogFilterOptions,
  LogRetentionConfig,
} from '@/core/types/models/LogEntry'
import { downloadCsv, generateTimestampedFileName } from '@/shared/utils/fileDownload'

/** Database name */
const DB_NAME = 'AdamCloudLogs'

/** Database version */
const DB_VERSION = 1

/** Object store name */
const STORE_NAME = 'logs'

/** Lazy-initialized database promise */
let dbPromise: Promise<IDBPDatabase> | null = null

/** Flag to track if retention pruning has run */
let hasRunRetention = false

/**
 * Gets or creates the IndexedDB database instance.
 * Uses lazy initialization to avoid blocking app startup.
 */
async function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Create the logs object store with auto-incrementing ID
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, {
            keyPath: 'id',
            autoIncrement: true,
          })

          // Create indexes for efficient querying
          store.createIndex('timestamp', 'timestamp', { unique: false })
          store.createIndex('level', 'level', { unique: false })
          store.createIndex('category', 'category', { unique: false })
          store.createIndex('correlationId', 'correlationId', { unique: false })
        }
      },
      blocked() {
        console.warn('[LogStorage] Database upgrade blocked by another tab')
      },
      blocking() {
        console.warn('[LogStorage] This tab is blocking a database upgrade')
      },
      terminated() {
        console.error('[LogStorage] Database connection unexpectedly terminated')
        dbPromise = null
      },
    })
  }

  return dbPromise
}

/**
 * Adds a new log entry to storage.
 *
 * @param entry - The log entry options (without id and timestamp)
 * @returns The created log entry with generated id and timestamp
 */
export async function addLog(entry: CreateLogEntryOptions): Promise<LogEntry> {
  const db = await getDb()

  const logEntry: Omit<LogEntry, 'id'> = {
    ...entry,
    timestamp: new Date().toISOString(),
  }

  const id = await db.add(STORE_NAME, logEntry)

  return { ...logEntry, id: id as number }
}

/**
 * Retrieves logs within a date range.
 *
 * @param from - Start date (inclusive)
 * @param to - End date (inclusive)
 * @returns Array of log entries within the date range
 */
export async function getLogsByDateRange(from: Date, to: Date): Promise<LogEntry[]> {
  const db = await getDb()
  const fromISO = from.toISOString()
  const toISO = to.toISOString()

  const allLogs = await db.getAllFromIndex(STORE_NAME, 'timestamp')

  return allLogs.filter(
    (log: LogEntry) => log.timestamp >= fromISO && log.timestamp <= toISO
  ) as LogEntry[]
}

/**
 * Retrieves all logs from storage.
 *
 * @param options - Optional filter options
 * @returns Array of log entries
 */
export async function getAllLogs(options?: LogFilterOptions): Promise<LogEntry[]> {
  const db = await getDb()

  let logs = (await db.getAllFromIndex(STORE_NAME, 'timestamp')) as LogEntry[]

  // Sort by timestamp descending (most recent first)
  logs = logs.sort((a, b) => b.timestamp.localeCompare(a.timestamp))

  // Apply filters if provided
  if (options) {
    if (options.levels?.length) {
      logs = logs.filter((log) => options.levels!.includes(log.level))
    }

    if (options.categories?.length) {
      logs = logs.filter((log) => options.categories!.includes(log.category))
    }

    if (options.fromDate) {
      const fromISO = options.fromDate.toISOString()
      logs = logs.filter((log) => log.timestamp >= fromISO)
    }

    if (options.toDate) {
      const toISO = options.toDate.toISOString()
      logs = logs.filter((log) => log.timestamp <= toISO)
    }

    if (options.searchTerm) {
      const searchLower = options.searchTerm.toLowerCase()
      logs = logs.filter((log) => log.message.toLowerCase().includes(searchLower))
    }

    if (options.limit && options.limit > 0) {
      logs = logs.slice(0, options.limit)
    }
  }

  return logs
}

/**
 * Retrieves the most recent logs.
 *
 * @param count - Number of recent logs to retrieve
 * @returns Array of the most recent log entries
 */
export async function getRecentLogs(count: number): Promise<LogEntry[]> {
  return getAllLogs({ limit: count })
}

/**
 * Deletes logs older than the specified number of days.
 *
 * @param days - Maximum age in days (default: 7)
 * @returns Number of deleted entries
 */
export async function pruneByAge(days = 7): Promise<number> {
  const db = await getDb()
  const cutoffDate = new Date()
  cutoffDate.setDate(cutoffDate.getDate() - days)
  const cutoffISO = cutoffDate.toISOString()

  const tx = db.transaction(STORE_NAME, 'readwrite')
  const store = tx.objectStore(tx.objectStoreNames[0])
  const index = store.index('timestamp')

  let deletedCount = 0
  let cursor = await index.openCursor()

  while (cursor) {
    const log = cursor.value as LogEntry
    if (log.timestamp < cutoffISO) {
      await cursor.delete()
      deletedCount++
    }
    cursor = await cursor.continue()
  }

  await tx.done

  if (deletedCount > 0) {
    console.info(`[LogStorage] Pruned ${deletedCount} logs older than ${days} days`)
  }

  return deletedCount
}

/**
 * Estimates the storage size of logs in bytes.
 * Uses JSON serialization for approximation.
 */
async function estimateStorageSize(): Promise<number> {
  const logs = await getAllLogs()
  return new Blob([JSON.stringify(logs)]).size
}

/**
 * Deletes oldest logs until storage is under the specified size limit.
 *
 * @param maxBytes - Maximum storage size in bytes (default: 10MB)
 * @returns Number of deleted entries
 */
export async function pruneBySize(maxBytes = 10 * 1024 * 1024): Promise<number> {
  let currentSize = await estimateStorageSize()

  if (currentSize <= maxBytes) {
    return 0
  }

  const db = await getDb()
  const tx = db.transaction(STORE_NAME, 'readwrite')
  const store = tx.objectStore(tx.objectStoreNames[0])
  const index = store.index('timestamp')

  let deletedCount = 0
  let cursor = await index.openCursor() // Opens in ascending order (oldest first)

  // Delete oldest entries until we're under the size limit
  while (cursor && currentSize > maxBytes) {
    const log = cursor.value as LogEntry
    const logSize = new Blob([JSON.stringify(log)]).size

    await cursor.delete()
    deletedCount++
    currentSize -= logSize

    cursor = await cursor.continue()
  }

  await tx.done

  if (deletedCount > 0) {
    console.info(`[LogStorage] Pruned ${deletedCount} logs to stay under ${maxBytes} bytes`)
  }

  return deletedCount
}

/**
 * Runs retention pruning (age-based first, then size-based fallback).
 * Only runs once per session to avoid performance impact.
 *
 * @param config - Optional retention configuration
 */
export async function runRetention(config?: Partial<LogRetentionConfig>): Promise<void> {
  if (hasRunRetention) {
    return
  }

  hasRunRetention = true

  const maxAgeDays = config?.maxAgeDays ?? 7
  const maxSizeBytes = config?.maxSizeBytes ?? 10 * 1024 * 1024

  try {
    // First, prune by age
    await pruneByAge(maxAgeDays)

    // Then, prune by size if still over limit
    await pruneBySize(maxSizeBytes)
  } catch (error) {
    console.error('[LogStorage] Retention pruning failed:', error)
    // Reset flag to allow retry on next initialization
    hasRunRetention = false
  }
}

/**
 * Clears all logs from storage.
 */
export async function clearAll(): Promise<void> {
  const db = await getDb()
  await db.clear(STORE_NAME)
  console.info('[LogStorage] All logs cleared')
}

/**
 * Gets the total count of logs in storage.
 */
export async function getLogCount(): Promise<number> {
  const db = await getDb()
  return db.count(STORE_NAME)
}

/**
 * Escapes a value for CSV format.
 */
function escapeCsvValue(value: unknown): string {
  if (value === null || value === undefined) {
    return ''
  }

  const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value)

  // Escape quotes and wrap in quotes if contains comma, newline, or quote
  if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`
  }

  return stringValue
}

/**
 * Converts logs to CSV format.
 *
 * @param logs - Array of log entries to convert
 * @returns CSV string
 */
export function logsToCSv(logs: LogEntry[]): string {
  const headers = ['ID', 'Timestamp', 'Level', 'Category', 'Message', 'Duration (ms)', 'Correlation ID', 'Metadata']

  const rows = logs.map((log) => [
    escapeCsvValue(log.id),
    escapeCsvValue(log.timestamp),
    escapeCsvValue(log.level),
    escapeCsvValue(log.category),
    escapeCsvValue(log.message),
    escapeCsvValue(log.duration),
    escapeCsvValue(log.correlationId),
    escapeCsvValue(log.metadata),
  ])

  return [headers.join(','), ...rows.map((row) => row.join(','))].join('\n')
}

/**
 * Exports all logs (or filtered logs) to a CSV file.
 *
 * @param options - Optional filter options
 */
export async function exportToCsv(options?: LogFilterOptions): Promise<void> {
  const logs = await getAllLogs(options)
  const csvContent = logsToCSv(logs)
  const fileName = generateTimestampedFileName('application-logs', 'csv')

  downloadCsv(csvContent, fileName)
}

/**
 * Log storage service singleton.
 * Provides all storage operations for application logs.
 */
export const logStorage = {
  addLog,
  getAllLogs,
  getRecentLogs,
  getLogsByDateRange,
  getLogCount,
  pruneByAge,
  pruneBySize,
  runRetention,
  clearAll,
  exportToCsv,
  logsToCSv,
}
